import { useLayoutEffect } from "react";

/**
 * Fade-and-lift reveal for landing sections.
 *
 * Deliberately conservative about hiding anything:
 *
 * - Elements start **visible** in the markup. This effect is what marks them
 *   `pending` (the only state the CSS hides), so with JS disabled, an old
 *   browser, or if this effect never runs, the whole page is readable.
 * - It runs in a layout effect and skips anything already inside the viewport,
 *   leaving it untouched. IntersectionObserver's first callback is async, so
 *   marking above-the-fold content `pending` first would flash it. Hero content
 *   therefore paints once, with no animation — which is also best for LCP.
 * - Under `prefers-reduced-motion: reduce` it does nothing at all.
 *
 * Elements opt in with `className="landing-reveal"`, and may set
 * `data-reveal-index` to stagger against their siblings (40ms each).
 *
 * @param {React.RefObject<HTMLElement>} rootRef container to search within
 */
export default function useRevealOnScroll(rootRef) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    // Respect the OS setting, and bail out where the API is missing.
    const prefersReduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const items = Array.from(root.querySelectorAll(".landing-reveal"));
    if (items.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const index = Number(el.dataset.revealIndex || 0);
          el.style.setProperty("--reveal-delay", `${index * 40}ms`);
          el.dataset.reveal = "shown";
          // Reveal once per element, never on every scroll pass.
          observer.unobserve(el);
        });
      },
      { threshold: 0.01, rootMargin: "0px 0px -10% 0px" }
    );

    items.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const alreadyVisible =
        rect.top < window.innerHeight && rect.bottom > 0;
      // Already on screen at first paint: leave it alone, no hide, no animation.
      if (alreadyVisible) return;
      el.dataset.reveal = "pending";
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [rootRef]);
}
